// Play and Pause SVG icons. Hidden from assistive tech: each sits inside a video card that
// is a button named "Play video" (IBM svg_graphics_labelled, TASKS #166).
const PLAY_SVG = `<svg aria-hidden="true" focusable="false" viewBox="0 0 17 20" fill="none" xmlns="http://www.w3.org/2000/svg">
  <path d="M16.5 10.5281C17.1667 10.1432 17.1667 9.18098 16.5 8.79608L1.5 0.135832C0.833332 -0.249069 0 0.232056 0 1.00186V18.3224C0 19.0922 0.833334 19.5733 1.5 19.1884L16.5 10.5281Z" fill="#006E37" />
</svg>`;

const PAUSE_ICON = `<svg aria-hidden="true" focusable="false" width="16" height="16" viewBox="0 0 24 24" fill="#006E37">
  <rect x="5" y="4" width="4" height="16"></rect>
  <rect x="15" y="4" width="4" height="16"></rect>
</svg>`;

class InstafeedVideoPlayer {
  constructor() {
    this.activeVideo = null;
    this.activeBtn = null;
    this.swiper = null; // ✅ keep swiper instance
    this.init();
  }

  init() {
    document
      .querySelectorAll(".video_card, .video_card_slider")
      .forEach((card) => {
        this.addVideoFunctionality(card);
      });

    this.initSwiper();
  }

  // ✅ Stop autoplay + disable swipe
  pauseSwiper() {
    if (!this.swiper) return;
    try {
      if (this.swiper.autoplay && this.swiper.autoplay.running)
        this.swiper.autoplay.stop();
      this.swiper.allowTouchMove = false;
    } catch (e) {}
  }

  // ✅ Start autoplay + enable swipe
  resumeSwiper() {
    if (!this.swiper) return;
    try {
      this.swiper.allowTouchMove = true;
      if (
        this.swiper.params.autoplay &&
        this.swiper.autoplay &&
        !this.swiper.autoplay.running
      ) {
        this.swiper.autoplay.start();
      }
    } catch (e) {}
  }

  ensureVideoLoaded(videoEl) {
    if (!videoEl) return;
    if (videoEl.dataset.loaded === "true") return;

    const sourceEl = videoEl.querySelector("source");
    if (sourceEl && sourceEl.dataset.src) {
      sourceEl.src = sourceEl.dataset.src;
      videoEl.load();
      videoEl.dataset.loaded = "true";
    }
  }

  stopVideo(videoEl, btnEl, posterEl) {
    if (!videoEl) return;

    videoEl.pause();
    videoEl.currentTime = 0;

    if (posterEl) posterEl.style.display = "block";
    if (btnEl) btnEl.innerHTML = PLAY_SVG;

    // ✅ Resume autoplay when video stops
    this.resumeSwiper();
  }

  startVideo(videoEl, btnEl, posterEl) {
    if (!videoEl) return;

    this.ensureVideoLoaded(videoEl);

    const source = videoEl.querySelector("source");
    if (!source || !source.src) {
      console.warn("No video src found for", videoEl);
      return;
    }

    if (posterEl) posterEl.style.display = "none";

    videoEl.muted = false;
    videoEl.volume = 1.0;
    videoEl.controls = false;
    videoEl.loop = false;

    // ✅ Pause swiper as soon as user tries to play
    this.pauseSwiper();

    const playPromise = videoEl.play();
    if (playPromise !== undefined) {
      playPromise
        .then(() => {
          if (btnEl) btnEl.innerHTML = PAUSE_ICON;
        })
        .catch((error) => {
          console.log("Video play failed:", error);
          if (posterEl) posterEl.style.display = "block";
          // ✅ If play failed, resume swiper
          this.resumeSwiper();
        });
    }

    this.activeVideo = videoEl;
    this.activeBtn = btnEl;
  }

  addVideoFunctionality(card) {
    const videoEl = card.querySelector(".video-player");
    const posterEl = card.querySelector(".video-poster");
    const btnEl = card.querySelector(".play_button, .play_button_slider");

    if (!videoEl || !btnEl || !posterEl) return;

    posterEl.style.display = "block";
    btnEl.innerHTML = PLAY_SVG;

    card.addEventListener("click", (e) => {
      e.preventDefault();

      // If clicking same video → pause
      if (videoEl === this.activeVideo && !videoEl.paused) {
        this.stopVideo(videoEl, btnEl, posterEl);
        this.activeVideo = null;
        this.activeBtn = null;
        return;
      }

      // Stop previous video
      if (this.activeVideo && this.activeVideo !== videoEl) {
        const parent = this.activeVideo.closest(
          ".video_card, .video_card_slider"
        );
        this.stopVideo(
          this.activeVideo,
          parent?.querySelector(".play_button, .play_button_slider"),
          parent?.querySelector(".video-poster")
        );
      }

      this.startVideo(videoEl, btnEl, posterEl);
    });

    // Keyboard activation — Enter / Space trigger the same click handler.
    // The video_card / video_card_slider element is role="button" tabindex="0",
    // so it receives focus; pair that with this listener for keyboard parity.
    card.addEventListener("keydown", (e) => {
      if (e.key === "Enter" || e.key === " " || e.code === "Space") {
        e.preventDefault();
        card.click();
      }
    });

    // When video ends → reset
    videoEl.addEventListener("ended", () => {
      this.stopVideo(videoEl, btnEl, posterEl);
      this.activeVideo = null;
      this.activeBtn = null;
    });

    // ✅ If user pauses video from any reason, resume swipe
    videoEl.addEventListener("pause", () => {
      if (videoEl !== this.activeVideo) return;
      // if paused before ending
      this.resumeSwiper();
    });
  }

  initSwiper() {
    this.swiper = new Swiper(".instafeed-swiper", {
      slidesPerView: 1.2,
      spaceBetween: 16,
      loop: true,
      centeredSlides: true,
      initialSlide: 0,
      autoplay: {
        delay: 2000,
        disableOnInteraction: false,
      },
      speed: 800,
      breakpoints: {
        750: { slidesPerView: 2.2, spaceBetween: 20, centeredSlides: true },
        990: {
          slidesPerView: 3,
          spaceBetween: 24,
          centeredSlides: false,
          autoplay: false,
        },
        1200: {
          slidesPerView: 4,
          spaceBetween: 30,
          centeredSlides: false,
          autoplay: false,
        },
      },
    });

    this.swiper.on("slideChangeTransitionStart", () => {
      if (!this.activeVideo) return;
      const parent = this.activeVideo.closest(
        ".video_card, .video_card_slider"
      );
      this.stopVideo(
        this.activeVideo,
        parent?.querySelector(".play_button, .play_button_slider"),
        parent?.querySelector(".video-poster")
      );
      this.activeVideo = null;
      this.activeBtn = null;
    });
  }
}

document.addEventListener("DOMContentLoaded", function () {
  new InstafeedVideoPlayer();
});

if (typeof Shopify !== "undefined" && Shopify.designMode) {
  document.addEventListener("shopify:section:load", function () {
    new InstafeedVideoPlayer();
  });
}
